import { useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ClientPayload } from 'api/apiClients';
import spinnerTitle from '../../others/spinnerTitle';
import SvgSave from '../../svg/SvgSave';

interface ClientEditFormProps {
    initialValues: ClientPayload;
    loading?: boolean;
    submitLabel: string;
    onSubmit: (payload: ClientPayload) => Promise<void>;
}

const ClientEditForm = ({ initialValues, loading = false, submitLabel, onSubmit }: ClientEditFormProps) => {
    const { t } = useTranslation(['Clients', 'common']);
    const [formData, setFormData] = useState<ClientPayload>({
        clientName: initialValues.clientName || '',
        clientPhone: initialValues.clientPhone || '',
        clientEmail: initialValues.clientEmail || '',
        clientAddress: initialValues.clientAddress || '',
        clientLocation: initialValues.clientLocation || '',
        clientDescription: initialValues.clientDescription || '',
        clientStatus: initialValues.clientStatus ?? 0,
    });
    const refClientName = useRef<HTMLInputElement>(null);

    const handleChange = (fieldName: keyof ClientPayload, value: string | number) => {
        setFormData((prev) => ({ ...prev, [fieldName]: value }));
    };

    useEffect(() => {
        setTimeout(() => {
            if (refClientName.current) {
                refClientName.current.focus();
            }
        }, 100);
    }, []);

    return (
        <Form
            className="d-flex flex-column h-100"
            onSubmit={async (e) => {
                e.preventDefault();
                await onSubmit(formData);
            }}
        >
            <div className="off-client-edit-form d-flex flex-column h-100">
                <div className="off-client-edit-content px-3">
                    <Form.Group className="mb-3">
                        <Form.Label>{t('edit.fields.clientName')}</Form.Label>
                        <Form.Control
                            required
                            ref={refClientName}
                            value={formData.clientName || ''}
                            onChange={(e) => handleChange('clientName', e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('edit.fields.clientPhone')}</Form.Label>
                        <Form.Control
                            value={formData.clientPhone || ''}
                            onChange={(e) => handleChange('clientPhone', e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('edit.fields.clientEmail')}</Form.Label>
                        <Form.Control
                            type="email"
                            value={formData.clientEmail || ''}
                            onChange={(e) => handleChange('clientEmail', e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('edit.fields.clientAddress')}</Form.Label>
                        <Form.Control
                            value={formData.clientAddress || ''}
                            onChange={(e) => handleChange('clientAddress', e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('edit.fields.clientLocation')}</Form.Label>
                        <Form.Control
                            value={formData.clientLocation || ''}
                            onChange={(e) => handleChange('clientLocation', e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('edit.fields.clientDescription')}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={formData.clientDescription || ''}
                            onChange={(e) => handleChange('clientDescription', e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('fields.status')}</Form.Label>
                        <Form.Select
                            value={formData.clientStatus ?? 0}
                            onChange={(e) => handleChange('clientStatus', Number(e.target.value))}
                        >
                            <option value={0}>{t('status.0')}</option>
                            <option value={1}>{t('status.1')}</option>
                        </Form.Select>
                    </Form.Group>
                </div>

                <div className="p-3 offcanvas-footer off-client-edit-footer">
                    <div className="d-flex justify-content-end">
                        <Button type="submit" variant="info" disabled={loading} className="min100px">
                            {spinnerTitle(
                                <>
                                    <SvgSave className="me-2 mtop-2" />
                                    {submitLabel}
                                </>,
                                loading,
                                t('common:buttons.saving'),
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </Form>
    );
};

export default ClientEditForm;
